<script>
import { mapActions } from 'vuex'

import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { GraphChart } from 'echarts/charts'

import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  LegendComponent,
  LegendScrollComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
  VisualMapComponent,
} from 'echarts/components'
import MChart, { THEME_KEY } from 'vue-echarts'

use([
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  LegendComponent,
  LegendScrollComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
  CanvasRenderer,
  VisualMapComponent,
  GraphChart,
])
export default {
  components: {
    MChart,
  },
  props: {
    playerId: {
      type: Number,
      default: () => null,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      options: {
        animationDurationUpdate: 500,
        animationEasingUpdate: 'quinticInOut',
        animationDelay: function(idx) {
          // delay for later data is larger
          return idx * 100
        },

        visualMap: {
          left: -5,
          top: 20,
          itemWidth: 10,
          min: 0.0,
          max: 100,
          calculable: true,
          inRange: {
            color: [
              'rgba(255, 0, 0, 0.6)',
              'rgba(255, 252, 0,0.6)',
              'rgba(0, 255, 0,0.6)',
            ],
          },
          textStyle: {
            color: this.$style['graph-text-color'],
          },
        },

        series: [
          {
            type: 'graph',
            layout: 'none',
            left: 60,
            // width: 260,
            // height: 100,
            width: 260,
            height: 150,
            symbolSize: 5,
            roam: false,
            label: {
              normal: {
                show: true,
              },
            },
            edgeSymbol: ['circle', 'none'],
            edgeLabel: {
              normal: {
                textStyle: {
                  fontSize: 20,
                },
              },
            },
            data: null,
            links: null,

            itemStyle: {
              emphasis: {
                scale: false,
                borderColor: '#fff',
                borderWidth: 1,
              },
            },
          },
        ],
      },
    }
  },
  provide: {
    [THEME_KEY]: 'light',
  },
  async created() {
    this.getPlayerStatData({ id: this.playerId })
      .then((response) => {
        if (typeof response !== 'undefined') {
          this.setGraphData(response)

          this.isLoading = false
          this.$emit('loaded', true)
        }
      })
      .catch((error) => {
        console.error(error, 'Opponent Ranking Performance Data fetch failed')
      })
  },
  methods: {
    ...mapActions('players', ['getPlayerStatData']),
    setGraphData(data) {
      if (
        typeof data.field_position !== 'undefined' &&
        Array.isArray(data.field_position)
      ) {
        // const targetposition = []
        const pointposition = [...data.field_position]

        const topLeft = {
          value: 100,
          x: 0,
          y: 0,
          symbol: 'none',
          itemStyle: {
            opacity: 0,
          },
        }

        const bottomLeft = {
          value: 100,
          x: 100,
          y: 0,
          symbol: 'none',
          itemStyle: {
            opacity: 0,
          },
        }

        const bottomRight = {
          value: 100,
          x: 100,
          y: 100,
          symbol: 'none',
          itemStyle: {
            opacity: 0,
          },
        }

        const topRight = {
          value: 100,
          x: 0,
          y: 100,
          symbol: 'none',
          itemStyle: {
            opacity: 0,
          },
        }
        pointposition.push(topLeft)
        pointposition.push(bottomLeft)
        pointposition.push(bottomRight)
        pointposition.push(topRight)

        this.options.series[0].data = pointposition.map((pos) => {
          return {
            x: pos.x,
            y: pos.y * 0.5 - 100,
            value: pos.value,
            itemStyle:
              typeof pos.itemStyle !== 'undefined' ? pos.itemStyle : {},
          }
        })
      }
    },
    onResize() {
      if (typeof this.$refs.chart !== 'undefined') this.$refs.chart.resize()
    },
  },
}
</script>

<template>
  <v-responsive
    v-if="playerId !== null && playerId"
    v-resize="onResize"
    aspect-ratio="4/3"
  >
    <div :class="$style.legend">
      {{ $t('Left') }}
      <svg
        :class="$style.legendArrow"
        xmlns="http://www.w3.org/2000/svg"
        width="98.942"
        height="10.132"
        viewBox="0 0 98.942 10.132"
      >
        <g transform="translate(-557.5 -2451.672)">
          <line
            x2="98"
            transform="translate(557.5 2456.5)"
            fill="none"
            stroke="#707070"
            stroke-width="1"
          />
          <line
            x1="7"
            transform="translate(651.025 2452.025) rotate(45)"
            fill="none"
            stroke="#707070"
            stroke-width="1"
          />
          <line
            x1="7.16"
            transform="translate(651.025 2461.45) rotate(-45)"
            fill="none"
            stroke="#707070"
            stroke-width="1"
          />
        </g>
      </svg>
      {{ $t('Right') }}
    </div>

    <div :class="$style.soccerField">
      <img
        ref="chartBackground"
        :class="$style.soccerFieldImage"
        alt="Soccer field"
        src="@assets/images/football_field_white.svg"
      />
      <MChart
        v-if="!isLoading"
        ref="chart"
        :class="$style.chart"
        :option="options"
      />
    </div>
  </v-responsive>
</template>

<style lang="scss" module>
@import '@design';

:export {
  graph-text-color: $color-text;
}

.legend {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0.5rem;
  margin-left: 2rem;
  font-size: 0.7rem;
}

.legendArrow {
  margin-right: 1rem;
  margin-left: 1rem;
}

.chart {
  margin: 0 auto;
}

.responsive {
  min-height: 15rem;
  padding: 2rem;
  overflow: visible;
}

.soccerFieldImage {
  position: absolute;
  top: -38px;
  left: 107px;
  width: 165px;
  height: 276px;
  background-color: rgba(0, 0, 0, 0.4);
  transform: rotate(90deg);
}

.soccerField {
  position: relative;
  width: 340px;
  height: 200px;
  margin-top: 2rem;
  margin-right: auto;
  margin-left: auto;
}
</style>
